import { Col, Flex, Row, TableProps } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import HeaderCard from 'components/HeaderCard';
import MultiSelect from 'components/MultiSelect';
import SelectComponent from 'components/Select';
import RBCountryFilter, {
  useRoleBasedCountryFilter,
} from 'components/UniversalFilters/RBCountryFilter';
import useAgronomistList from 'hooks/useAgronomistList';
import useDashboardQuery, { TDashboard } from 'hooks/useDashboardQuery';
import useSeasonIndex from 'hooks/useSeasonIndex';
import useSubDistrictList from 'hooks/useSubDistrictList';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Season, TaskAssignment } from 'types/common';
import { SEASONS_STATUS } from 'utils/constants';
import { isMobileScreenSize } from 'utils/helpers';
import DashboardTable from '../awd-dashboard/components/DashboardTable';
import DetailDrawer from '../awd-dashboard/components/DetailDrawer';
import MobileDashboardTable from '../awd-dashboard/components/MobileDashboardTable';
import TaskDrawer from '../awd-dashboard/components/TaskDrawer';

export type SelectTaskData = Partial<Omit<TDashboard, 'season'>> & {
  task?: TaskAssignment;
  season?: Season;
};

const SalesGroupProgress = () => {
  // Table Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Drawer
  const [openTaskDrawer, setOpenTaskDrawer] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState<SelectTaskData>();
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [selectedData, setSelectedData] = useState<TDashboard>();
  // Filters
  const [agronomist, setAgronomist] = useState<string[]>([]);
  const [subDistrict, setSubDistrict] = useState<string[]>([]);
  const [seasonStatus, setSeasonStatus] = useState<string>();
  const [seasonIndex, setSeasonIndex] = useState<string>();
  const [country, setCountry] = useRoleBasedCountryFilter();
  const [sortedInfo, setSortedInfo] = useState<SorterResult<TDashboard>>({});
  const [sort, setSort] = useState('');
  // Hooks
  const { t } = useTranslation();
  // Filter Dropdown Queries
  const { agronomistFilterOptions, isLoading: isLoadingAgronomist } =
    useAgronomistList(country);
  const { subDistrictOptions, isLoading: isLoadingSubdistrict } =
    useSubDistrictList(country);
  const { seasonIndexFilterOptions, currentSeason } = useSeasonIndex(country);
  // Data Query
  const { data, isLoading } = useDashboardQuery({
    page: currentPage - 1,
    size: pageSize,
    country,
    seasonTypeId: seasonIndex,
    groupType: 'SALES_GROUP',
    agronomist: agronomist?.join(',') || undefined,
    subDistrict: subDistrict?.join(',') || undefined,
    seasonStatus,
    sort,
  });

  const handleChange: TableProps<TDashboard>['onChange'] = (
    _,
    __,
    tableSort,
  ) => {
    const sort = tableSort as SorterResult<TDashboard>;
    if (sort.order) {
      setSort(`${sort.columnKey},${sort.order === 'ascend' ? 'asc' : 'desc'}`);
    } else {
      setSort('');
    }
    setSortedInfo(sort);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleCloseDrawer = () => {
    setOpenTaskDrawer(false);
    setOpenDetailDrawer(false);
    setSelectedTaskData(undefined);
    setSelectedData(undefined);
  };

  useEffect(() => {
    setSeasonIndex(currentSeason?.id);
  }, [currentSeason]);

  return (
    <div className="dashboard-container">
      <HeaderCard
        headerLabel={t('salesGroups.header')}
        headerInfo={t('salesGroups.description')}
        filters={
          <Flex gap={20} align="center" wrap="wrap">
            <RBCountryFilter selectedCountry={country} onChange={setCountry} />
            <SelectComponent
              label={t('common.filters.season')}
              defaultValue={currentSeason?.seasonCode}
              options={seasonIndexFilterOptions}
              value={seasonIndex}
              onChange={setSeasonIndex}
            />
          </Flex>
        }
      />

      <Row>
        <Col span={24} className="table-container">
          <Row className="filters">
            <MultiSelect
              label={t('common.filters.agronomist')}
              options={agronomistFilterOptions}
              defaultValue={agronomist}
              value={agronomist}
              onChange={setAgronomist}
              disabled={isLoadingAgronomist}
              allowClear
            />

            <MultiSelect
              label={t('genericForm.Sub-District')}
              options={subDistrictOptions}
              defaultValue={subDistrict}
              value={subDistrict}
              onChange={setSubDistrict}
              disabled={isLoadingSubdistrict}
              allowClear
            />

            <SelectComponent
              label={t('farmerGroupList.seasonStatus')}
              placeholder={t('genericForm.select', {
                value: t('farmerGroupList.seasonStatus'),
              })}
              options={SEASONS_STATUS}
              value={seasonStatus}
              onChange={setSeasonStatus}
              allowClear
            />
          </Row>

          {isMobileScreenSize ? (
            <MobileDashboardTable
              data={data?.data?.content || []}
              loading={isLoading}
              total={data?.data?.totalElements || 0}
              pageSize={pageSize}
              current={currentPage}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              onSelectTask={(data: SelectTaskData) => {
                setSelectedTaskData(data);
                setOpenTaskDrawer(true);
              }}
              onSelectRow={(data: TDashboard) => {
                setSelectedData(data);
                setOpenDetailDrawer(true);
              }}
              isSalesGroup
            />
          ) : (
            <DashboardTable
              data={data?.data?.content || []}
              loading={isLoading}
              total={data?.data?.totalElements || 0}
              pageSize={pageSize}
              currentPage={currentPage}
              sortedInfo={sortedInfo}
              selectedRow={selectedData || (selectedTaskData as TDashboard)}
              handleChange={handleChange}
              handlePageChange={handlePageChange}
              onSelectTask={(data: SelectTaskData) => {
                setSelectedTaskData(data);
                setOpenTaskDrawer(true);
              }}
              onSelectRow={(data: TDashboard) => {
                setSelectedData(data);
                setOpenDetailDrawer(true);
              }}
              isSalesGroup
            />
          )}

          <TaskDrawer
            data={selectedTaskData}
            open={openTaskDrawer}
            onClose={handleCloseDrawer}
          />
          <DetailDrawer
            data={selectedData}
            open={openDetailDrawer}
            onClose={handleCloseDrawer}
            isSalesGroup
          />
        </Col>
      </Row>
    </div>
  );
};

export default SalesGroupProgress;
